import React, { use, useEffect, useState } from "react";
import { useLoading } from "@/contexts";
import { useNavigate, useParams } from "react-router-dom";
import {
  plansService,
  PlansRecord,
  PaymentHistoryEnum,
  IBGECity,
  statesOfBrazilService,
  postalCodeOfBrazilService,
} from "@/services";
import { formatPrice, optionsBrazilianStates, isValidEmail } from "@/utils";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Swipe } from "@/components";
import "./Checkout.css";

type CheckoutFormInnerProps = {
  onConfirm: (stripe: any, elements: any) => void;
};

const CheckoutFormInner: React.FC<CheckoutFormInnerProps> = ({ onConfirm }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isReady, setIsReady] = useState(false);

  return (
    <div className="stripe-payment-wrapper">
      <div className="stripe-payment-element">
        <PaymentElement
          options={{ layout: "tabs" }}
          onReady={() => setIsReady(true)}
        />
      </div>

      <div
        className={`modal-actions ${!stripe || !elements || !isReady ? "disabled" : ""}`}
      >
        <button
          className="btn-primary"
          onClick={() => onConfirm(stripe, elements)}
          disabled={!stripe || !elements || !isReady}
        >
          Finalizar pagamento
        </button>
      </div>
    </div>
  );
};

const CheckoutScreen: React.FC = () => {
  const { planId } = useParams();
  const { withLoading } = useLoading();

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY_PROD || "");

  const navigate = useNavigate();

  const steps = ["Plano", "Dados", "Revisão", "Finalizar"];

  const [plan, setPlan] = useState<PlansRecord | null>(null);
  const [step, setStep] = useState(0);
  const [availableMethods, setAvailableMethods] = useState<string[]>([]);
  const [clientSecret, setClientSecret] = useState<string>("");

  const [checkoutStatus, setCheckoutStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    taxId: "",
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
    },
  });

  const [cities, setCities] = useState<IBGECity[]>([]);

  const [touched, setTouched] = useState<Record<string, boolean>>({
    name: false,
    email: false,
    "address.state": false,
    "address.city": false,
    "address.street": false,
    "address.postalCode": false,
  });

  const [cardTouched, setCardTouched] = useState(false);

  useEffect(() => {
    const initCheckout = async () => {
      await withLoading(async () => {
        await Promise.all([loadPlans()]);
      });
    };

    initCheckout();
  }, []);

  useEffect(() => {
    const loadCities = async () => {
      if (!formData.address.state) {
        setCities([]);
        return;
      }

      try {
        const result = await statesOfBrazilService.getCitiesByState(
          formData.address.state,
        );
        setCities(result);
      } catch (error) {
        console.error("Erro ao carregar cidades:", error);
        setCities([]);
      }
    };

    loadCities();
  }, [formData.address.state]);

  const loadPlans = async () => {
    if (!planId) return;

    const plans = await plansService.getActivePlans();

    const selected = plans.find((p) => p.id === planId);

    if (selected) setPlan(selected);
  };

  const next = () => setStep((s) => s + 1);

  const back = () => setStep((s) => s - 1);

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name } = e.target;
    // Se o name for "address.city", ele salvará a chave exatamente assim no objeto touched
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleCepBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e);

    const cep = e.target.value.replace(/\D/g, "");

    if (cep.length === 8) {
      await withLoading(async () => {
        try {
          const addressData =
            await postalCodeOfBrazilService.getAddressByCep(cep);

          if (addressData) {
            setFormData((prev) => ({
              ...prev,
              address: {
                ...prev.address,
                state: addressData.state,
                city: addressData.city,
                street: addressData.street,
              },
            }));

            setTouched((prev) => ({
              ...prev,
              "address.state": true,
              "address.city": true,
              "address.street": true,
            }));
          }
        } catch (error) {
          console.error("Erro ao auto-preencher endereço:", error);
        }
      });
    }
  };

  const errors = React.useMemo(() => {
    return {
      name: !formData.name ? "Nome é obrigatório." : null,
      email: !formData.email
        ? "Informe seu e-mail."
        : isValidEmail(formData.email)
          ? null
          : "E-mail inválido.",
      taxId: !formData.taxId ? "CPF/CNPJ é obrigatório." : null,
      postalCode: !formData.address.postalCode ? "CEP é obrigatório." : null,
      state: !formData.address.state ? "Selecione o estado." : null,
      city: !formData.address.city ? "Selecione a cidade." : null,
      street: !formData.address.street ? "Endereço é obrigatório." : null,
    };
  }, [formData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev: any) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleNextStep2 = () => {
    setTouched({
      name: true,
      email: true,
      taxId: true,
      "address.postalCode": true,
      "address.state": true,
      "address.city": true,
      "address.street": true,
    });

    const hasErrors = Object.values(errors).some((error) => error !== null);

    if (hasErrors) {
      alert("Por favor, corrija os erros no formulário antes de continuar.");
      return;
    }

    next();
  };

  const handlePreparePayment = async () => {
    if (!plan) return;

    await withLoading(async () => {
      try {
        const resCreate: any = await plansService.createPayment({
          amount: plan.price,
          planId: plan.id,
        });

        setClientSecret(resCreate.data.clientSecret);
        next();
      } catch (e) {
        alert("Erro ao iniciar pagamento.");
      }
    });
  };

  const finalizeCheckout = async (stripe: any, elements: any) => {
    await withLoading(async () => {
      try {
        if (!stripe || !elements) {
          alert(
            "Houve um erro ao processar seu pagamento. Tente novamente mais tarde.",
          );
          return;
        }

        const result = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
        });

        if (result.error) {
          alert(result.error.message);
          return;
        }

        setCheckoutStatus("success");
      } catch (error) {
        console.error(error);
        setCheckoutStatus("error");
      }
    });
  };

  if (checkoutStatus === "success") {
    return (
      <div className="container checkout-page">
        <div className="checkout-card success-card">
          <div className="status-icon">✅</div>
          <h2>Pedido Recebido!</h2>
          <p>
            Seu pedido para o plano <strong>{plan?.name}</strong> foi gerado com
            sucesso.
          </p>

          <div className="result-box">
            <button
              className="btn-primary"
              onClick={() => navigate("/pagamentos")}
            >
              Começar a usar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (checkoutStatus === "error") {
    return (
      <div className="container checkout-page">
        <div className="checkout-card error-card">
          <div className="status-icon">❌</div>
          <h2>Ops! Algo deu errado</h2>
          <p>Não conseguimos processar seu pagamento neste momento.</p>
          <div className="result-box error">
            <p>Pode ser um problema temporário com a operadora ou conexão.</p>
          </div>

          <div className="checkout-error-actions">
            <button
              className="btn-primary"
              onClick={() => setCheckoutStatus("idle")}
            >
              Tentar Novamente
            </button>
            <button className="btn-secondary" onClick={() => navigate("/")}>
              Voltar para Início
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return <div className="container">Carregando plano...</div>;
  }

  return (
    <div className="container checkout-page">
      <Swipe steps={steps} activeStep={step} />

      {step === 0 && (
        <div className="checkout-card">
          <h2>Confirmar plano</h2>

          <div className="plan-summary">
            <h3>{plan.name}</h3>

            <p>{plan.description}</p>

            <strong>
              {formatPrice(plan.price)} / {plan.durationMonths} meses
            </strong>
          </div>

          <button className="btn-primary" onClick={next}>
            Continuar
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="checkout-card">
          <h2>Seus Dados</h2>
          <p className="step-description">
            Preencha as informações para a fatura.
          </p>

          <div className="checkout-form">
            <div className="edit-form-group">
              <label>Nome Completo</label>
              <input
                type="text"
                name="name"
                className={touched.name && errors.name ? "error" : ""}
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.name && errors.name && (
                <span className="error-message">{errors.name}</span>
              )}
            </div>

            <div className="edit-form-group">
              <label>E-mail</label>
              <input
                type="email"
                name="email"
                className={touched.email && errors.email ? "error" : ""}
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ex.: nome@email.com.br"
              />
              {touched.email && errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="edit-form-group">
              <label>CPF ou CNPJ</label>
              <input
                type="text"
                name="taxId"
                className={touched.taxId && errors.taxId ? "error" : ""}
                placeholder="Apenas números"
                value={formData.taxId}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.taxId && errors.taxId && (
                <span className="error-message">{errors.taxId}</span>
              )}
            </div>

            <div className="form-row">
              <div className="edit-form-group ">
                <label>CEP</label>
                <input
                  type="text"
                  name="address.postalCode"
                  className={
                    touched["address.postalCode"] && errors.postalCode
                      ? "error"
                      : ""
                  }
                  placeholder="00000-000"
                  value={formData.address.postalCode}
                  onChange={handleChange}
                  onBlur={handleCepBlur}
                />
                {touched["address.postalCode"] && errors.postalCode && (
                  <span className="error-message">{errors.postalCode}</span>
                )}
              </div>

              <div className="edit-form-group">
                <label>Estado</label>
                <select
                  name="address.state"
                  // Use colchetes para chaves que contêm pontos e acesse o erro plano
                  className={
                    touched["address.state"] && errors.state ? "error" : ""
                  }
                  value={formData.address.state || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Selecione seu estado</option>
                  {optionsBrazilianStates.map((uf) => (
                    <option key={uf.value} value={uf.value}>
                      {uf.label}
                    </option>
                  ))}
                </select>
                {/* Ajuste na exibição da mensagem */}
                {touched["address.state"] && errors.state && (
                  <span className="error-message">{errors.state}</span>
                )}
              </div>
            </div>

            <div className="edit-form-group">
              <label>Cidade</label>
              <select
                name="address.city"
                className={
                  touched["address.city"] && errors.city ? "error" : ""
                }
                value={formData.address.city || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!formData.address.state}
              >
                <option value="">
                  {formData.address.state
                    ? "Selecione sua cidade"
                    : "Selecione primeiro o estado"}
                </option>

                {cities.map((city) => (
                  <option key={city.id} value={city.nome}>
                    {city.nome}
                  </option>
                ))}
              </select>
              {touched["address.city"] && errors.city && (
                <span className="error-message">{errors.city}</span>
              )}
            </div>

            <div className="edit-form-group">
              <label>Endereço</label>
              <input
                type="text"
                name="address.street"
                className={
                  touched["address.street"] && errors.street ? "error" : ""
                }
                placeholder="Rua, número e bairro"
                value={formData.address.street}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn-secondary" onClick={back}>
              Voltar
            </button>
            <button className="btn-primary" onClick={handleNextStep2}>
              Continuar
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="checkout-card">
          <h2>Revisão do pedido</h2>
          <div className="review-box">
            <p>
              Plano: <strong>{plan.name}</strong>
            </p>
            <p>
              Valor: <strong>{formatPrice(plan.price)}</strong>
            </p>
          </div>
          <div className="modal-actions">
            <button className="btn-secondary" onClick={back}>
              Voltar
            </button>
            <button className="btn-primary" onClick={handlePreparePayment}>
              Confirmar
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="checkout-card">
          <h2>Finalizar assinatura</h2>
          <p>Clique abaixo para concluir o pagamento</p>

          {clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance: { theme: "night" } }}
            >
              <CheckoutFormInner onConfirm={finalizeCheckout} />
            </Elements>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckoutScreen;
